import { supabase } from '../supabaseClient.js';

// One place to call `activate_free_plan()` and one interpretation of its
// answer, shared by every surface that can start a Free plan (onboarding step
// 7, the checkout flow, and the Settings plan picker). Before this existed the
// three copies disagreed: onboarding finished the wizard on `already_granted`
// while checkout showed a warning and left the user on the form with no way
// forward.
//
// The RPC is the eligibility authority. It keys the one-time trial on the
// immutable credit ledger, not on `subscription_info.has_used_free_plan`, so a
// flag reset (the legacy `monthly_free_subscription_plan_reset` job is still
// live until Phase 3) cannot mint a second grant — it answers
// `already_granted` instead.
//
// The rule this module encodes:
//
//   granted            -> success. Credits were deposited.
//   already_granted    -> NOT a success and NOT an error. The account is
//   paid_plan_active      already provisioned; nothing was deposited. Say so
//                         plainly, then let the caller finish its flow and
//                         refresh, so the user is never stranded on a form
//                         that can only ever return the same answer.
//   anything else      -> error. The caller keeps the user on the form.
//
// `settled` is the flag callers act on: true means "stop asking, move on".
// `granted` stays separate so no caller can accidentally report credits that
// were never deposited.

const PROVISIONED_REASONS = ['already_granted', 'paid_plan_active'];

const MESSAGES = {
  already_granted:
    "You're already on the Free plan — your one-time trial credits were granted earlier, " +
    'so no new credits were added.',
  // Keep the guidance, not just the diagnosis: the pre-unification Settings
  // copy told the user what to do about it, and that is the half worth having.
  // It reads correctly from every surface, since cancelling is the only route
  // from a paid plan to the Free plan wherever the user happens to be.
  paid_plan_active:
    'You already have an active paid plan, so the Free plan was not activated. ' +
    'Cancel your paid plan first and the Free plan will take over at the end of ' +
    'the current billing period.',
};

/**
 * @param {object} plan  The FREE entry of SUBSCRIPTION_PLANS, for credit copy.
 * @returns {Promise<{settled: boolean, granted: boolean, reason: ?string,
 *                    message: string, severity: 'success'|'info'|'error'}>}
 */
export async function activateFreePlan(plan) {
  const { data, error } = await supabase.rpc('activate_free_plan');

  if (error) {
    return {
      settled: false,
      granted: false,
      reason: 'rpc_error',
      message: error.message || 'The Free plan could not be activated. Please try again.',
      severity: 'error',
    };
  }

  const result = Array.isArray(data) ? data[0] : data;

  if (!result) {
    return {
      settled: false,
      granted: false,
      reason: 'no_result',
      message: 'Free plan activation returned no result. Please try again.',
      severity: 'error',
    };
  }

  if (result.granted) {
    const endsOn = result.subscription_end_date
      ? new Date(result.subscription_end_date).toLocaleDateString()
      : null;
    const credits = plan?.monthlyCredits;
    return {
      settled: true,
      granted: true,
      reason: null,
      message: endsOn
        ? `Free plan activated. Your ${credits} trial credits are available until ${endsOn}.`
        : `Free plan activated. Your ${credits} trial credits are available.`,
      severity: 'success',
    };
  }

  if (PROVISIONED_REASONS.includes(result.reason)) {
    return {
      settled: true,
      granted: false,
      reason: result.reason,
      message: MESSAGES[result.reason],
      severity: 'info',
    };
  }

  return {
    settled: false,
    granted: false,
    reason: result.reason || 'refused',
    message: 'The Free plan could not be activated. Please try again.',
    severity: 'error',
  };
}

export default activateFreePlan;
